import React from 'react';
import styled, { css } from 'styled-components';
import { bindHover, bindPopover, bindTrigger, PopupState as PopupStateType } from 'material-ui-popup-state/hooks';
import PopupState from 'material-ui-popup-state';
import { ClickAwayListener } from '@material-ui/core';
import { mediaQueries } from 'shared/styles';

import SvgChevronIcon from 'src/assets/chevron-icon';
import { Typography } from 'src/typography';
import {
  buttonResetStyles,
  PopoverNavButton,
  PopoverNavItem,
  StyledHoverPopover,
} from 'src/components/menu-sort-v2/menu-sort-v2.styles';

import { SPECIALS_SORT_OPTIONS, SpecialsSortKey } from './specials-sort.types';

type SpecialsSortDropdownProps = {
  activeSortKey: SpecialsSortKey | undefined;
  onSortChange: (key: SpecialsSortKey) => void;
};

export function SpecialsSortDropdown({ activeSortKey, onSortChange }: SpecialsSortDropdownProps): JSX.Element {
  const activeLabel = SPECIALS_SORT_OPTIONS.find((opt) => opt.key === activeSortKey)?.label;

  const handleClick = (key: SpecialsSortKey, popupState: PopupStateType): void => {
    onSortChange(key);
    void popupState.close();
  };

  return (
    <PopupState variant='popover' popupId='specialsSortPopover'>
      {(popupState: PopupStateType) => (
        <ClickAwayListener
          onClickAway={() => {
            if (popupState.isOpen) {
              popupState.close();
            }
          }}
          mouseEvent='onMouseDown'
          touchEvent='onTouchStart'
        >
          <div>
            <SortTypeButton
              {...bindHover(popupState)}
              {...bindTrigger(popupState)}
              aria-haspopup='true'
              aria-expanded={popupState.isOpen}
              data-testid='specials-sort-popover-toggle'
            >
              <SortLabel size='small'>Sort by:</SortLabel>
              {activeLabel && (
                <SortLabel size='small' data-testid='specials-sort-select-label'>
                  {activeLabel}
                </SortLabel>
              )}
              <SvgChevronIcon height='16px' width='16px' />
            </SortTypeButton>

            <StyledHoverPopover
              {...bindPopover(popupState)}
              anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
              transformOrigin={{ horizontal: 'left', vertical: 'top' }}
              data-testid='specials-sort-popover'
              $offsetTop
            >
              <nav>
                <SortByLabelItem $isActive>Sort by</SortByLabelItem>
                {SPECIALS_SORT_OPTIONS.map(({ key, label }) => (
                  <PopoverNavButton
                    key={key}
                    role='menuitem'
                    onClick={() => handleClick(key, popupState)}
                    $isActive={activeSortKey === key}
                    data-testid={`specials-sort-select-${key}`}
                  >
                    {label}
                  </PopoverNavButton>
                ))}
              </nav>
            </StyledHoverPopover>
          </div>
        </ClickAwayListener>
      )}
    </PopupState>
  );
}

const sortSelectionStyle = css`
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  svg {
    color: var(--dcm-content-header-icon-color, #828a8f);
  }
`;

const SortTypeButton = styled.button`
  ${sortSelectionStyle}
  ${buttonResetStyles}
  align-items: center;
  color: var(--dcm-content-header-text-color, ${({ theme }) => theme.colors.primaryBlack});
  display: flex;
  position: relative;
  gap: 8px;

  @media ${mediaQueries.largePhone} {
    gap: 4px;
  }

  &::after {
    content: '';
    height: 8px;
    left: 0;
    position: absolute;
    top: 100%;
    width: 100%;
  }
`;

const SortByLabelItem = styled(PopoverNavItem)`
  color: var(--dcm-content-header-icon-color, #828a8f);
`;

const SortLabel = styled(Typography.Label)`
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
`;
